/**
 * EventDetailParts — small presentational helpers shared between the events
 * list and the dedicated event detail page. Extracted from EventsPage when
 * the modal was promoted to a routed page so both surfaces render speakers,
 * agendas and attendee chips with identical styling.
 */
import React, { useMemo, useState } from 'react';
import { Star } from 'lucide-react';
import MemberAvatar from '@/components/MemberAvatar';
import Linkify from '@/lib/linkify';
import RichText from '@/components/RichText';
import { stableAgendaItemId, parseAgendaTime } from '@/lib/agendaUtils';

export function AttendeeAvatar({ attendee, size = 'sm' }) {
  const px = size === 'sm' ? 32 : 48;
  return (
    <span title={attendee.name}>
      <MemberAvatar
        name={attendee.name}
        picture={attendee.picture}
        size={px}
        ringClassName={size === 'sm' ? 'ring-2 ring-white' : ''}
      />
    </span>
  );
}

export function BioParagraphs({ text }) {
  if (!text) return null;
  return <RichText html={text} className="text-xs text-slate-500 leading-relaxed compact" />;
}

export function SpeakerCard({ speaker, onViewProfile }) {
  const isLinked = !!speaker.user_id;
  return (
    <div
      className={`flex gap-4 items-start p-4 bg-white rounded-xl border border-slate-100 shadow-sm ${isLinked ? 'cursor-pointer hover:border-brand-navy/30 hover:shadow-md transition-all' : ''}`}
      data-testid={`speaker-card-${speaker.name?.replace(/\s+/g, '-').toLowerCase()}`}
      onClick={() => { if (isLinked && onViewProfile) onViewProfile(speaker); }}>
      <div className="w-16 h-16 flex-shrink-0">
        <MemberAvatar name={speaker.name} picture={speaker.headshot_url} size={64} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <h4 className={`font-semibold text-sm ${isLinked ? 'text-brand-navy' : 'text-slate-800'}`}>{speaker.name}</h4>
          {isLinked && <span className="text-[9px] bg-brand-navy/10 text-brand-navy px-1.5 py-0.5 rounded-full font-medium">View Profile</span>}
        </div>
        {speaker.bio && <div className="mt-1.5"><BioParagraphs text={speaker.bio} /></div>}
      </div>
    </div>
  );
}

export function AgendaTimeline({
  agenda,
  onViewSpeaker,
  // v2 — optional. When provided, enables location filter, My Agenda star
  // toggle, and clash warnings.
  locations,          // [{id, name, color}] admin-managed per-event list
  myPicks,            // Set<string> of starred item_ids
  onToggleStar,       // (itemId, isStarring, item) => void
  canStar,            // boolean — only true for logged-in users on dedicated page
  clashes,            // Map<item_id, string[]> from computeClashes()
  // BriteLab registration (Phase 1):
  britelabCounts,     // { item_id: {count, max, is_britelab} }
  myBritelabRegs,     // Set<string> of item_ids the user is registered for
  eventRsvpd,         // boolean — is the current user RSVP'd to the parent event
  onToggleRegister,   // (item, isRegistering) => void
}) {
  // Rooms derived from the agenda; merged with admin-defined locations so
  // an empty room can still appear if the admin registered it.
  const rooms = useMemo(() => {
    const seen = new Set();
    const ordered = [];
    (locations || []).forEach(l => {
      const name = (l.name || '').trim();
      if (name && !seen.has(name)) { seen.add(name); ordered.push(name); }
    });
    (agenda || []).forEach(item => {
      const r = (item.room || '').trim();
      if (r && !seen.has(r)) { seen.add(r); ordered.push(r); }
    });
    return ordered;
  }, [agenda, locations]);

  const [activeRoom, setActiveRoom] = useState('ALL');

  if (!agenda?.length) return null;

  let filtered;
  if (activeRoom === 'ALL') filtered = agenda;
  else if (activeRoom === '__MY__') {
    filtered = agenda.filter(i => myPicks && myPicks.has(stableAgendaItemId(i)));
  } else {
    filtered = agenda.filter(i => (i.room || '').trim() === activeRoom);
  }

  const normaliseSpeakers = (item) => {
    if (Array.isArray(item.speakers) && item.speakers.length > 0) {
      return item.speakers.map(s => ({
        user_id: s.user_id || '',
        name: s.name || '',
        picture: s.headshot_url || s.picture || '',
        bio: s.bio || '',
      }));
    }
    if (item.speaker || item.speaker_user_id) {
      return [{
        user_id: item.speaker_user_id || '',
        name: item.speaker || '',
        picture: item.speaker_headshot_url || '',
        bio: item.speaker_bio || '',
      }];
    }
    return [];
  };

  const myCount = myPicks ? agenda.filter(i => myPicks.has(stableAgendaItemId(i))).length : 0;
  const clashCount = clashes ? [...clashes.keys()].filter(id => myPicks?.has(id)).length : 0;
  const showMyChip = canStar;

  return (
    <div className="space-y-4" data-testid="event-agenda-timeline">
      {(rooms.length > 1 || showMyChip) && (
        <div
          className="-mx-1 px-1 overflow-x-auto scrollbar-thin scrollbar-thumb-slate-200"
          data-testid="agenda-room-tabs"
          role="tablist"
          aria-label="Filter agenda by location"
        >
          <div className="flex items-center gap-1.5 pb-1 min-w-min">
            {['ALL', ...(showMyChip ? ['__MY__'] : []), ...rooms].map(r => {
              const isActive = activeRoom === r;
              const isMy = r === '__MY__';
              const label = r === 'ALL' ? 'All' : isMy ? 'My Agenda' : r;
              const testKey = r === 'ALL' ? 'all' : isMy ? 'my' : r.replace(/\s+/g, '-').toLowerCase();
              const count = r === 'ALL'
                ? agenda.length
                : isMy ? myCount
                : agenda.filter(i => (i.room || '').trim() === r).length;
              return (
                <button
                  key={r}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => setActiveRoom(r)}
                  data-testid={`agenda-room-tab-${testKey}`}
                  className={`
                    flex items-center gap-1.5 whitespace-nowrap
                    px-3 py-1.5 rounded-full text-xs font-semibold
                    transition-all border
                    ${isActive
                      ? isMy ? 'bg-amber-500 text-white border-amber-500 shadow-sm' : 'bg-brand-navy text-white border-brand-navy shadow-sm'
                      : 'bg-white text-slate-600 border-slate-200 hover:border-brand-navy/30 hover:text-brand-navy'}
                  `}
                >
                  {isMy ? <Star className="w-3 h-3 fill-current" /> : r !== 'ALL' && <span aria-hidden="true">📍</span>}
                  <span>{label}</span>
                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${isActive ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-500'}`}>
                    {count}
                  </span>
                  {isMy && clashCount > 0 && (
                    <span data-testid="my-agenda-clash-badge" className="text-[9px] font-bold px-1 py-0.5 rounded-full bg-red-500 text-white">
                      {clashCount} clash{clashCount > 1 ? 'es' : ''}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {filtered.length === 0 ? (
        <p className="text-sm text-slate-500 italic" data-testid="agenda-empty-filtered">
          {activeRoom === '__MY__'
            ? 'Star sessions below to build your personal agenda.'
            : `No sessions listed for ${activeRoom}.`}
        </p>
      ) : (
        <div className="space-y-0" data-testid="agenda-timeline-list">
          {filtered.map((item, idx) => {
            const speakers = normaliseSpeakers(item);
            const isLast = idx === filtered.length - 1;
            const itemId = stableAgendaItemId(item);
            const isStarred = myPicks && myPicks.has(itemId);
            const clashesForItem = clashes && isStarred ? clashes.get(itemId) : null;
            return (
              <div key={idx} className="flex gap-4 relative" data-testid={`agenda-row-${idx}`}>
                {!isLast && <div className="absolute left-[19px] top-8 bottom-0 w-px bg-slate-200" />}
                <div className="flex-shrink-0 w-10 flex flex-col items-center pt-1">
                  <div className="w-3 h-3 rounded-full bg-brand-navy border-2 border-white shadow-sm z-10" />
                </div>
                <div className="flex-1 pb-5 min-w-0">
                  <div className="flex items-baseline gap-3 flex-wrap">
                    <span className="text-xs font-bold text-brand-navy bg-brand-navy/5 px-2 py-0.5 rounded">{item.time}</span>
                    <h4 className="text-sm font-semibold text-slate-800 break-words [overflow-wrap:anywhere]">{item.title}</h4>
                    {item.room && (
                      <span className="text-[10px] font-medium text-brand-sage bg-brand-sage/10 px-2 py-0.5 rounded-full" data-testid={`agenda-room-${idx}`}>
                        📍 {item.room}
                      </span>
                    )}
                    {(() => {
                      const bl = britelabCounts && britelabCounts[itemId];
                      if (!bl?.is_britelab) return null;
                      const atCap = bl.max && bl.count >= bl.max;
                      const near = bl.max && bl.count / bl.max >= 0.8;
                      const colour = atCap ? 'bg-red-100 text-red-700' : near ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600';
                      return (
                        <span
                          data-testid={`britelab-count-${idx}`}
                          className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${colour}`}>
                          {bl.count}{bl.max ? ` / ${bl.max}` : ''} registered
                        </span>
                      );
                    })()}
                    {canStar && (
                      <div className="ml-auto flex items-center gap-1.5">
                        {(() => {
                          const bl = britelabCounts && britelabCounts[itemId];
                          if (!bl?.is_britelab || !onToggleRegister) return null;
                          const isRegistered = myBritelabRegs && myBritelabRegs.has(itemId);
                          const atCap = bl.max && bl.count >= bl.max;
                          if (isRegistered) {
                            return (
                              <button
                                type="button"
                                onClick={() => onToggleRegister(item, false)}
                                data-testid={`britelab-registered-${idx}`}
                                className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-0.5 rounded-full border-2 border-green-500 text-green-700 bg-green-50 hover:bg-red-50 hover:border-red-400 hover:text-red-700 group transition-colors">
                                <span className="group-hover:hidden">Registered ✓</span>
                                <span className="hidden group-hover:inline">Unregister</span>
                              </button>
                            );
                          }
                          if (atCap) {
                            return (
                              <span
                                data-testid={`britelab-full-${idx}`}
                                className="inline-flex items-center text-[11px] font-bold px-2.5 py-0.5 rounded-full bg-slate-200 text-slate-500 cursor-not-allowed">
                                FULL
                              </span>
                            );
                          }
                          return (
                            <button
                              type="button"
                              onClick={() => onToggleRegister(item, true)}
                              title={eventRsvpd ? '' : 'You need a place at the event to book BriteLabs'}
                              data-testid={`britelab-register-${idx}`}
                              className="inline-flex items-center text-[11px] font-semibold px-2.5 py-0.5 rounded-full bg-brand-navy text-white hover:bg-brand-navy/90 transition-colors">
                              Register
                            </button>
                          );
                        })()}
                        {!(onToggleRegister && britelabCounts?.[itemId]?.is_britelab) && (
                        <button
                          type="button"
                          onClick={() => onToggleStar && onToggleStar(itemId, !isStarred, item)}
                          data-testid={`agenda-star-${idx}`}
                          aria-pressed={isStarred}
                          aria-label={isStarred ? 'Remove from My Agenda' : 'Add to My Agenda'}
                          className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full transition-colors
                            ${isStarred
                              ? 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                              : 'bg-slate-100 text-slate-500 hover:bg-amber-50 hover:text-amber-700'}`}>
                          <Star className={`w-3 h-3 ${isStarred ? 'fill-current' : ''}`} />
                          {isStarred ? 'Starred' : 'Add'}
                        </button>
                        )}
                      </div>
                    )}
                  </div>
                  {clashesForItem && clashesForItem.length > 0 && (
                    <div
                      data-testid={`agenda-clash-${idx}`}
                      className="mt-1.5 text-[11px] font-medium text-red-700 bg-red-50 border border-red-200 rounded-md px-2 py-1 inline-flex items-center gap-1">
                      ⚠️ Clashes with: {clashesForItem.join(', ')}
                    </div>
                  )}
                  {speakers.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`agenda-speakers-${idx}`}>
                      {speakers.map((spk, sIdx) => {
                        const key = `${idx}-${sIdx}`;
                        if (spk.user_id) {
                          return (
                            <button key={key} type="button"
                              onClick={() => onViewSpeaker && onViewSpeaker(spk)}
                              data-testid={`agenda-speaker-link-${idx}-${sIdx}`}
                              className="inline-flex items-center gap-1.5 text-xs font-medium text-brand-navy hover:underline transition-colors bg-brand-navy/5 hover:bg-brand-navy/10 rounded-full pl-0.5 pr-2 py-0.5">
                              <MemberAvatar name={spk.name} picture={spk.picture} size={20} />
                              <span>{spk.name}</span>
                            </button>
                          );
                        }
                        return spk.name ? (
                          <span key={key}
                            className="text-xs font-medium text-brand-navy/80 px-2 py-0.5 bg-slate-100 rounded-full"
                            data-testid={`agenda-speaker-text-${idx}-${sIdx}`}>
                            {spk.name}
                          </span>
                        ) : null;
                      })}
                    </div>
                  )}
                  {item.description && <RichText html={item.description} className="text-xs text-slate-500 mt-1 break-words [overflow-wrap:anywhere] compact" />}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export const formatEventDate = (d) => {
  try { return new Date(d).toLocaleDateString('en-GB', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }); }
  catch { return d; }
};

export const formatEventFormat = (f) => {
  if (!f) return '';
  const norm = String(f).toLowerCase().replace(/[-_\s]/g, '');
  if (norm === 'inperson') return 'In person';
  if (norm === 'online') return 'Online';
  if (norm === 'hybrid') return 'Hybrid';
  return f.charAt(0).toUpperCase() + f.slice(1);
};
